import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { format, addMonths, getYear, endOfMonth } from "date-fns";
import { XMarkIcon, ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { Actions as PAYMENT_ACTIONS } from "@/redux/actions/paymentActions";
import { Actions as EMPLOYEE_ACTIONS } from "@/redux/actions/employeeActions";
import TransactionRow from "../TransactionRow";
import { CSVLink } from "react-csv";

const presentField = (field, data) => {
	const value = data[field.field];
	switch (field.type) {
		case "date":
			return value ? format(new Date(value), "dd/MM/yyyy") : "N/A";
		case "boolean":
			return value ? "Yes" : "No";
		default:
			return value ?? "N/A";
	}
};

const TransactionHistoryModal = ({
	toggleTransactionHistoryModal,
	modalStatus,
	transaction,
	getTransactionHistory,
	activeUsers,
	fields,
	headers,
	downloadPDF,
	employeesGetListWithSalary,
}) => {
	useEffect(() => {
		if (modalStatus) {
			getTransactionHistory(transaction?.id);
			employeesGetListWithSalary({
				from: format(new Date(transaction?.date), "yyyy-MM-01"),
				to: format(endOfMonth(new Date(transaction?.date)), "yyyy-MM-dd"),
			});
		}
	}, [modalStatus]);

	if (!modalStatus) return null;

	return (
		<div
			className="modal"
			tabIndex="-1"
			role="dialog"
			aria-hidden="true"
			style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
		>
			<div
				className="modal-dialog"
				style={{ width: "85%" }}
			>
				<div className="modal-content">
					<div
						className="block block-themed block-transparent remove-margin-b"
						style={{ padding: 0 }}
					>
						<div
							className="block-header bg-primary-dark"
							style={{ padding: 20, position: "sticky", top: 0, zIndex: 50 }}
						>
							<ul className="block-options">
								<li>
									<button
										onClick={() => {
											downloadPDF(transaction.id);
										}}
									>
										<i className="fa fa-file-pdf" />
									</button>
								</li>
								<li>
									<CSVLink
										filename="Single Transaction History.csv"
										title="Download Transaction History"
										data={[headers, ...activeUsers]}
										className="btn btn-success"
										style={{ marginBottom: 20 }}
									>
										<ArrowDownTrayIcon
											className="w-6 h-6 mr-3"
											style={{ verticalAlign: "text-bottom" }}
										/>
									</CSVLink>
								</li>
								<li>
									<button
										type="button"
										onClick={() => toggleTransactionHistoryModal(false, null)}
									>
										<XMarkIcon className="h-6 w-6 mr-3 text-white" />
									</button>
								</li>
							</ul>
							<h3 className="block-title">{transaction?.description}</h3>
						</div>
						<div
							className="block-content"
							style={{ padding: 0 }}
						>
							<table className="table table-responsive">
								<thead>
									<tr>
										{headers.map((header, index) => {
											return (
												<th
													key={index}
													className={index > 0 ? "text-center" : "text-left"}
												>
													{header}
												</th>
											);
										})}
									</tr>
								</thead>
								<tbody>
									{activeUsers?.map((transactionEmployee) => {
										return (
											<TransactionRow
												fields={fields}
												key={transactionEmployee?.agreementId}
												transactionEmployee={transactionEmployee}
											/>
										);
									})}
								</tbody>
							</table>
						</div>
						<div className="clearfix" />
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const transaction = state?.payment?.transactionHistoryModalTransaction;
	const employees = state.employees?.employeeListWithSalary || [];
	const activeUsers = state?.payment?.transactionHistory || [];

	const departments = state.company?.settings?.departments || [];
	const departmentsEnabled = state.company?.settings?.enabledDepartments;
	const hasDepartments = typeof departmentsEnabled === "boolean" ? departmentsEnabled : departments?.length > 0;

	const activeUsersWithDetails = activeUsers.map((user) => {
		const employee = employees.find((emp) => emp.id === user.agreementId);

		return {
			...user,
			name: employee ? employee?.employeeName + " " + employee?.employeeSurname : "Unknown Employee",
			reference: employee?.idNo || employee?.socialSecurity,
			department: employee?.departmentName,
			engagementDate: employee?.engagementDate,
			terminationDate: employee?.terminationDate,
		};
	});

	const fields = [
		{ header: "Employee", field: "name" },
		{ header: "Reference", field: "reference" },
		hasDepartments ? { header: "Department", field: "department" } : null,
		{ header: "Employment Type", field: "employmentStatus" },
		{ header: "Payslip", field: "hasPayslip", type: "boolean" },
		{ header: "Leave", field: "hasLeave", type: "boolean" },
		{ header: "Timesheet", field: "hasTimesheet", type: "boolean" },
		{ header: "Engagement Date", field: "engagementDate", type: "date" },
		{ header: "Termination Date", field: "terminationDate", type: "date" },
	].filter(Boolean);

	const headers = fields.map(({ header }) => header);
	const dataRows = activeUsersWithDetails.map((user) => fields.map((field) => presentField(field, user)));

	return {
		modalStatus: state?.payment?.transactionHistoryModal,
		transaction,
		activeUsers: dataRows,
		headers,
		fields,
	};
};

const mapDispatchToProps = (dispatch) => ({
	toggleTransactionHistoryModal: (status, transaction) =>
		dispatch(PAYMENT_ACTIONS.toggleTransactionHistoryModal(status, transaction)),
	getTransactionHistory: (transactionId) => dispatch(PAYMENT_ACTIONS.getTransactionHistory(transactionId)),
	downloadPDF: (data) => dispatch(PAYMENT_ACTIONS.downloadTransactionHistoryPDF(data)),
	employeesGetListWithSalary: ({ from, to }) =>
		dispatch(
			EMPLOYEE_ACTIONS.employeesGetListWithSalary(
				{
					from: from ? from : format(addMonths(new Date(), -2), "yyyy-MM-01"), // get list from last 2 months
					to: to ? to : format(new Date(), "yyyy-12-31"),
				},
				{ includeTax: true },
			),
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(TransactionHistoryModal);
